import React from "react";
import { message, Progress, Tag, Collapse, Divider } from "antd";
import {
    ClockCircleOutlined, // Esperando
    SyncOutlined,       // En proceso
    CheckCircleOutlined, // Finalizado
    CloseCircleOutlined, // Error
    DownOutlined,
} from '@ant-design/icons';
import axios from "axios";
import { Card, Row, Col } from "reactstrap";

const tagColors = {
    "Esperando": "blue",
    "En proceso": "orange",
    "Finalizado": "green",
    "Error": "red",
};
const tagIcons = {
    "Esperando": <ClockCircleOutlined />,
    "En proceso": <SyncOutlined spin />,
    "Finalizado": <CheckCircleOutlined />,
    "Error": <CloseCircleOutlined />,
};

class BillingResources extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            billingResources: [],
            collapseOpen: false,
            showChildren: {},
        };
        this.percentageCalc = this.percentageCalc.bind(this);
        this.listBillingResources = this.listBillingResources.bind(this);
    }

    componentDidMount() {
        this.listBillingResources();
        this.interval = setInterval(this.listBillingResources, 1500);
    }

    componentWillUnmount() {
        clearInterval(this.interval);
    }

    percentageCalc(valor, minimo, maximo) {
        return Math.round(((valor - minimo) / (maximo - minimo)) * 100);
    }

    async listBillingResources() {
        try {
            const csrfToken = localStorage.getItem("token");
            const response = await axios.post(
                "https://oficina.nadunet.es/api/rest/nadunet_billing_resources/list_active_processes",
                {},
                {
                    headers: {
                        "X-CSRF-Token": csrfToken,
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );
            if (response.data && response.data.ok) {
                const resourcesObject = Object.values(response.data.processes);
                const formatted = resourcesObject.map((res) => ({
                    id: res.id,                  // Asegura un identificador único
                    name: res.name,
                    progress: this.percentageCalc(res.progress, res.min, res.max),
                    status: res.status,
                    status_message: res.status_message || "Sin mensaje",
                    children: res.children
                        ? Object.values(res.children).map((c) => ({
                            id: c.id,
                            ...c,
                            progress: this.percentageCalc(c.progress, c.min, c.max),
                        }))
                        : [],
                    start: res.start,
                    start_ts: res.start_ts,
                    end: res.end,
                    duration: res.duration,
                }));
                this.setState({ billingResources: formatted });
            }
        } catch (error) {
            console.error("Error fetching billing resources:", error);
            message.error("Error al obtener los recursos de facturación");
        }
    }

    countStatuses() {
        const counts = { Esperando: 0, "En proceso": 0, Finalizado: 0, Error: 0 };
        this.state.billingResources.forEach((r) => {
            if (counts[r.status] !== undefined) counts[r.status]++;
        });
        return counts;
    }

    toggleChildren = (path) => {
        this.setState((prev) => ({
            showChildren: {
                ...prev.showChildren,
                [path]: !prev.showChildren[path],
            },
        }));
    };

    formatElapsed(start) {
        const startMs = typeof start === 'number' ? start : new Date(start).getTime();
        const startDate = new Date(startMs);
        const now = new Date();
        const diffMs = now - startDate;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    }

    renderResource(resource, level = 0) {
        const { showChildren } = this.state;
        const hasChildren = resource.children && resource.children.length > 0;

        // Determinar duración y fechas a mostrar
        let displayDuration;
        let displayDates;
        if (resource.status === 'Finalizado') {
            displayDuration = resource.duration;
            displayDates = `${resource.start} > ${resource.end}`;
        } else {
            displayDuration =
                resource.start_ts != null
                    ? this.formatElapsed(resource.start_ts * 1000)
                    : '--';

            displayDates = resource.start;
        }

        return (
            <Card
                key={resource.id}
                style={{
                    padding: 10,
                    backgroundColor: level % 2 === 1 ? '#f9f9f9' : 'white',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 'large' }}>{resource.name}</span>
                    <Tag
                        icon={tagIcons[resource.status]}
                        color={tagColors[resource.status]}
                        style={{ minWidth: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        {resource.status}
                    </Tag>
                </div>
                <p style={{ margin: 0, color: '#888' }}>{resource.status_message}</p>
                <div style={{ paddingLeft: 4, paddingRight: 4 }}>
                    <Progress
                        percent={resource.progress}
                        status={
                            resource.status === 'Error'
                                ? 'exception'
                                : resource.status === 'Finalizado'
                                    ? 'success'
                                    : resource.status === 'Esperando'
                                        ? 'normal'
                                        : 'active'
                        }
                    />
                </div>

                <div style={{ justifyContent: "space-between", display: "flex" }}>
                    <div>Duración: {displayDuration}</div>
                    <div>{displayDates}</div>
                </div>

                {hasChildren && (
                    <React.Fragment>
                        <Divider
                            style={{ margin: '10px 0', cursor: 'pointer' }}
                            onClick={() => this.toggleChildren(resource.id)}
                        >
                            <DownOutlined
                                style={{
                                    transform: showChildren[resource.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </Divider>
                        {showChildren[resource.id] &&
                            resource.children.map((child) =>
                                this.renderResource(child, level + 1)
                            )}
                    </React.Fragment>
                )}
            </Card>
        );
    }

    render() {
        const { billingResources, collapseOpen } = this.state;
        return billingResources.length > 0 ? (
            <Row>
                <Col xs={12}>
                    <Card className="card-plain">
                        <Collapse
                            bordered={false}
                            style={{ backgroundColor: 'transparent' }}
                            activeKey={collapseOpen ? '1' : '0'}
                        >
                            <Collapse.Panel
                                key="1"
                                className="card-profile-panel"
                                header={
                                    <div
                                        onClick={() => this.setState({ collapseOpen: !collapseOpen })}
                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%' }}
                                    >
                                        <h5 style={{ margin: 0, flex: 1 }}>Procesos activos</h5>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                            {Object.entries(this.countStatuses()).map(([status, count]) => (
                                                <Tag
                                                    key={status}
                                                    icon={tagIcons[status]}
                                                    color={tagColors[status]}
                                                    style={{ display: 'flex', alignItems: 'center', fontSize: 14, minWidth: 120, justifyContent: 'center' }}
                                                >
                                                    {status}: {count}
                                                </Tag>
                                            ))}
                                        </div>
                                    </div>
                                }
                            >
                                <div style={{ marginTop: 10 }}>
                                    {billingResources.map((res) =>
                                        this.renderResource(res, 0)
                                    )}
                                </div>
                            </Collapse.Panel>
                        </Collapse>
                    </Card>
                </Col>
            </Row>
        ) : (
            <div />
        );
    }
}

export default BillingResources;



// WEBPACK FOOTER //
// ./src/views/Bills/components/BillingResources.jsx